import { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Document {
  id: string;
  name: string;
  type: string;
  size: string;
  uploadedAt: string;
}

export default function Knowledge() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchDocuments = async () => {
      try {
        const response = await api.get('/api/v1/knowledge');
        if (!cancelled) {
          const data = response.data;
          setDocuments(Array.isArray(data) ? data : data.documents || []);
        }
      } catch {
        if (!cancelled) {
          setDocuments([
            { id: '1', name: 'Market Research Report.pdf', type: 'PDF', size: '2.4 MB', uploadedAt: '1 hour ago' },
            { id: '2', name: 'Product Specs.docx', type: 'DOCX', size: '1.1 MB', uploadedAt: '3 hours ago' },
            { id: '3', name: 'Competitor Analysis.xlsx', type: 'XLSX', size: '856 KB', uploadedAt: '1 day ago' },
          ]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDocuments();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowModal(false);
    };
    if (showModal) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [showModal]);

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const getExtension = (name: string): string => name.split('.').pop()?.toUpperCase() || 'FILE';

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await api.post('/api/v1/knowledge/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newDoc: Document = response.data?.document || response.data;
      setDocuments((prev) => [newDoc, ...prev]);
    } catch {
      const fallbackDoc: Document = {
        id: Date.now().toString(),
        name: selectedFile.name,
        type: getExtension(selectedFile.name),
        size: formatSize(selectedFile.size),
        uploadedAt: 'Just now',
      };
      setDocuments((prev) => [fallbackDoc, ...prev]);
    } finally {
      setSelectedFile(null);
      setUploading(false);
      setShowModal(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'PDF': return '📄';
      case 'DOCX': return '📝';
      case 'XLSX': return '📊';
      default: return '📁';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Knowledge Base</h1>
        <button onClick={() => setShowModal(true)} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
          + Upload Document
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">Name</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">Type</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">Size</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">Uploaded</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {documents.map((doc) => (
              <tr key={doc.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{getTypeIcon(doc.type)}</span>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{doc.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{doc.type}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{doc.size}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{doc.uploadedAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="knowledge-upload-title">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-lg w-full mx-4 p-6">
            <h2 id="knowledge-upload-title" className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Upload Document</h2>

            {selectedFile ? (
              <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-4">
                <span className="text-2xl">{getTypeIcon(getExtension(selectedFile.name))}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{formatSize(selectedFile.size)}</p>
                </div>
                <button onClick={() => setSelectedFile(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            ) : (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                  dragOver
                    ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-purple-400'
                }`}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <svg className="w-10 h-10 mx-auto text-gray-400 dark:text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Drag & drop your file here</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">PDF, DOCX, XLSX, TXT, CSV, MD</p>
                <input id="file-input" type="file" className="hidden" accept=".pdf,.docx,.xlsx,.txt,.csv,.md" onChange={handleFileSelect} />
              </div>
            )}

            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setShowModal(false); setSelectedFile(null); }} className="px-4 py-2 text-sm rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600">Cancel</button>
              <button onClick={handleUpload} disabled={!selectedFile || uploading} className="px-4 py-2 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                {uploading && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
